import * as z from 'zod/mini';
import { PORT_GROUP_LABEL_MAX_LENGTH, PORT_GROUP_MAX_MEMBERS } from '../constants.ts';
import { idSchema, notesSchema, timestampSchema } from './common.ts';

// A group of a device's numbered ports that act together (ROADMAP C5):
//   lag    – several ports bonded into one logical link (an aggregation
//            group, "ag0"); `memberPorts` names the ports in the bond and
//            there is no monitor port.
//   mirror – a monitoring session that copies the traffic of one or more
//            source ports (`memberPorts`) to one destination the analyser
//            sits on (`monitorPort`).
// Both hang off one device (`itemId`); the trace, the topology, and the
// device page read them so an aggregated link reads as one logical link with
// its members and a mirror is labelled.

export const PORT_GROUP_KINDS = ['lag', 'mirror'] as const;
export type PortGroupKind = (typeof PORT_GROUP_KINDS)[number];
export const portGroupKindSchema = z.enum(PORT_GROUP_KINDS);

export const PORT_GROUP_KIND_LABELS: Readonly<Record<PortGroupKind, string>> = {
  lag: 'Link aggregation',
  mirror: 'Port mirror',
};

/** The smallest a bond may be: one port is not an aggregation. */
export const LAG_MIN_MEMBERS = 2;

const portNumberSchema = z.int().check(z.positive());
const memberPortsSchema = z.array(portNumberSchema).check(z.maxLength(PORT_GROUP_MAX_MEMBERS));
const labelSchema = z.string().check(z.maxLength(PORT_GROUP_LABEL_MAX_LENGTH));

export const portGroupSchema = z.object({
  id: idSchema,
  clientId: idSchema,
  itemId: idSchema,
  kind: portGroupKindSchema,
  label: labelSchema,
  /** A LAG's bonded ports, or a mirror's source ports. */
  memberPorts: memberPortsSchema,
  /** A mirror's destination (monitor) port; null on a LAG. */
  monitorPort: z.nullable(portNumberSchema),
  notes: notesSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type PortGroup = z.infer<typeof portGroupSchema>;

export const portGroupInputSchema = z.strictObject({
  itemId: idSchema,
  kind: portGroupKindSchema,
  label: z._default(labelSchema, ''),
  memberPorts: memberPortsSchema,
  monitorPort: z._default(z.nullable(portNumberSchema), null),
  notes: notesSchema,
});
export type PortGroupInput = z.infer<typeof portGroupInputSchema>;

export const portGroupEnvelopeSchema = z.object({ portGroup: portGroupSchema });
